import { ResourceBaseService } from "@/core/common/resource-base/resource-base.service";
import { ApiHttpClient } from "@/core/common/http/api-http-client";
import { EmployeeOnboardingProcessDto } from "@/core/features/employee-onboarding/employee-onboarding-process.dto";
import { UpdateOnboardingProcessMetaDto } from "@/core/features/employee-onboarding/update-onboarding-process-meta.dto";
import { ConfigureOnboardingRequirementsDto } from "@/core/features/employee-onboarding/configure-onboarding-requirements.dto";
import { HttpRequest } from "@/core/common/http/http-request";
import { plainToInstance } from "class-transformer";
import { ReviewEmployeeDocumentDto } from "@/core/features/employee-documents/review-employee-document.dto";
import { EmployeeOnboardingAcceptanceDto } from "@/core/features/employee-onboarding/employee-onboarding-acceptance.dto";
import { SaveOnboardingFormDto } from "@/core/features/employee-onboarding/save-onboarding-form.dto";
import { StartOnboardingSessionDto } from "@/core/features/employee-onboarding/start-onboarding-session.dto";
import { OnboardingFormDraft } from "@/core/features/employee-onboarding/onboarding-form-draft.interface";
import { AcceptSignatureRequirementDto } from "@/core/features/employee-onboarding/accept-signature-requirement.dto";
import { PdfAcroFormWidgetDto } from "@/core/features/employee-onboarding/pdf-acro-form-widget.dto";
import { OnboardingSignaturePreviewDto } from "@/core/features/employee-onboarding/onboarding-signature-preview.dto";
import { EmployeeDocumentVersionDto } from "@/core/features/employee-documents/employee-document-version.dto";
import { EmployeeOnboardingSignatureRequirementDto } from "@/core/features/employee-onboarding/employee-onboarding-signature-requirement.dto";
import { SignaturePlacementDto } from "@/core/features/employee-document-categories/signature-placement.dto";
import { UpsertSignaturePlacementsDto } from "@/core/features/employee-document-categories/upsert-signature-placements.dto";
import { OnboardingProgressDetailsDto } from "@/core/features/employee-onboarding/onboarding-progress-details.dto";
import { OnboardingListSummaryDto } from "@/core/features/employee-onboarding/onboarding-list-summary.dto";
import { PendingReviewDocumentCountDto } from "@/core/features/employee-onboarding/pending-review-document-count.dto";
import { SendOnboardingInvitationDto } from "@/core/features/employee-onboarding/send-onboarding-invitation.dto";
import { MarkEmployeeReadyDto } from "@/core/features/employee-onboarding/mark-employee-ready.dto";

export class EmployeeOnboardingResource extends ResourceBaseService<
  EmployeeOnboardingProcessDto,
  UpdateOnboardingProcessMetaDto,
  ConfigureOnboardingRequirementsDto
> {
  protected EntityDto = EmployeeOnboardingProcessDto;
  protected CreateEntityDto = UpdateOnboardingProcessMetaDto;
  protected UpdateEntityDto = ConfigureOnboardingRequirementsDto;

  constructor(protected readonly apiHttpClient: ApiHttpClient) {
    super(apiHttpClient, "/employee-onboarding");
  }

  async createOrGetProcess(userId: string): Promise<EmployeeOnboardingProcessDto> {
    const process = await this.apiHttpClient.request<null, EmployeeOnboardingProcessDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/users/" + userId + "/process",
        method: "POST",
        auth: true,
      })
    );

    return plainToInstance(EmployeeOnboardingProcessDto, process);
  }

  async findProcess(processId: string): Promise<EmployeeOnboardingProcessDto> {
    const process = await this.apiHttpClient.request<null, EmployeeOnboardingProcessDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/processes/" + processId,
        auth: true,
      })
    );

    return plainToInstance(EmployeeOnboardingProcessDto, process);
  }

  async updateMeta(processId: string, dto: UpdateOnboardingProcessMetaDto): Promise<EmployeeOnboardingProcessDto> {
    const process = await this.apiHttpClient.request<UpdateOnboardingProcessMetaDto, EmployeeOnboardingProcessDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/processes/" + processId + "/meta",
        method: "PATCH",
        auth: true,
        body: plainToInstance(UpdateOnboardingProcessMetaDto, dto, { excludeExtraneousValues: true }),
      })
    );

    return plainToInstance(EmployeeOnboardingProcessDto, process);
  }

  async configureRequirements(
    processId: string,
    dto: ConfigureOnboardingRequirementsDto
  ): Promise<EmployeeOnboardingProcessDto> {
    const process = await this.apiHttpClient.request<ConfigureOnboardingRequirementsDto, EmployeeOnboardingProcessDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/processes/" + processId + "/requirements",
        method: "PATCH",
        auth: true,
        body: plainToInstance(ConfigureOnboardingRequirementsDto, dto, { excludeExtraneousValues: true }),
      })
    );

    return plainToInstance(EmployeeOnboardingProcessDto, process);
  }

  async synchronizeRequirements(processId: string): Promise<EmployeeOnboardingProcessDto> {
    const process = await this.apiHttpClient.request<null, EmployeeOnboardingProcessDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/processes/" + processId + "/requirements/synchronize",
        method: "POST",
        auth: true,
      })
    );

    return plainToInstance(EmployeeOnboardingProcessDto, process);
  }

  async sendInvitation(
    processId: string,
    dto: SendOnboardingInvitationDto
  ): Promise<{ token: string; process: EmployeeOnboardingProcessDto }> {
    const response = await this.apiHttpClient.request<
      SendOnboardingInvitationDto,
      { token: string; process: EmployeeOnboardingProcessDto }
    >(
      HttpRequest.create({
        url: this.resourceUrl + "/processes/" + processId + "/invitation",
        method: "POST",
        auth: true,
        body: plainToInstance(SendOnboardingInvitationDto, dto, { excludeExtraneousValues: true }),
      })
    );

    return {
      token: response.token,
      process: plainToInstance(EmployeeOnboardingProcessDto, response.process),
    };
  }

  async sendReminder(processId: string): Promise<{ token: string; process: EmployeeOnboardingProcessDto }> {
    const response = await this.apiHttpClient.request<null, { token: string; process: EmployeeOnboardingProcessDto }>(
      HttpRequest.create({
        url: this.resourceUrl + "/processes/" + processId + "/reminder",
        method: "POST",
        auth: true,
      })
    );

    return {
      token: response.token,
      process: plainToInstance(EmployeeOnboardingProcessDto, response.process),
    };
  }

  async reviewDocument(
    processId: string,
    versionId: string,
    dto: ReviewEmployeeDocumentDto
  ): Promise<{ reviewed: true }> {
    return this.apiHttpClient.request<ReviewEmployeeDocumentDto, { reviewed: true }>(
      HttpRequest.create({
        url: this.resourceUrl + "/processes/" + processId + "/review/" + versionId,
        method: "PATCH",
        auth: true,
        body: plainToInstance(ReviewEmployeeDocumentDto, dto, { excludeExtraneousValues: true }),
      })
    );
  }

  async uploadReviewedDocument(processId: string, categoryId: string, file: File): Promise<EmployeeDocumentVersionDto> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("categoryId", categoryId);
    formData.append("filename", file.name);

    const version = await this.apiHttpClient.request<FormData, EmployeeDocumentVersionDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/processes/" + processId + "/documents/reviewed",
        method: "POST",
        auth: true,
        body: formData,
        type: "form-data",
      })
    );

    return plainToInstance(EmployeeDocumentVersionDto, version);
  }

  async markEmployeeReady(processId: string, dto: MarkEmployeeReadyDto): Promise<EmployeeOnboardingProcessDto> {
    const process = await this.apiHttpClient.request<MarkEmployeeReadyDto, EmployeeOnboardingProcessDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/processes/" + processId + "/mark-ready",
        method: "POST",
        auth: true,
        body: plainToInstance(MarkEmployeeReadyDto, dto, { excludeExtraneousValues: true }),
      })
    );

    return plainToInstance(EmployeeOnboardingProcessDto, process);
  }

  async recalculateProgress(processId: string): Promise<{ progressPercent: number }> {
    return this.apiHttpClient.request<null, { progressPercent: number }>(
      HttpRequest.create({
        url: this.resourceUrl + "/processes/" + processId + "/recalculate-progress",
        method: "POST",
        auth: true,
      })
    );
  }

  async getProgressDetails(processId: string): Promise<OnboardingProgressDetailsDto> {
    const progressDetails = await this.apiHttpClient.request<null, OnboardingProgressDetailsDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/processes/" + processId + "/progress",
        auth: true,
      })
    );

    return plainToInstance(OnboardingProgressDetailsDto, progressDetails);
  }

  async getReviewQueueCount(): Promise<number> {
    const result = await this.apiHttpClient.request<null, { count: number }>(
      HttpRequest.create({
        url: this.resourceUrl + "/review-queue/count",
        auth: true,
      })
    );

    return result.count;
  }

  async getListSummary(): Promise<OnboardingListSummaryDto> {
    const summary = await this.apiHttpClient.request<null, OnboardingListSummaryDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/list-summary",
        auth: true,
      })
    );

    return plainToInstance(OnboardingListSummaryDto, summary);
  }

  async getPendingReviewDocumentCounts(userIds: string[]): Promise<PendingReviewDocumentCountDto[]> {
    if (!userIds.length) {
      return [];
    }

    const counts = await this.apiHttpClient.request<null, PendingReviewDocumentCountDto[]>(
      HttpRequest.create({
        url: this.resourceUrl + "/pending-review-counts",
        auth: true,
        query: `userIds=${userIds.map((userId) => encodeURIComponent(userId)).join(",")}`,
      })
    );

    return plainToInstance(PendingReviewDocumentCountDto, counts);
  }

  async startSession(dto: StartOnboardingSessionDto): Promise<{ token: string; processId: string; status: string }> {
    return this.apiHttpClient.request<StartOnboardingSessionDto, { token: string; processId: string; status: string }>(
      HttpRequest.create({
        url: this.resourceUrl + "/portal/start-session",
        method: "POST",
        body: plainToInstance(StartOnboardingSessionDto, dto, { excludeExtraneousValues: true }),
      })
    );
  }

  async getPortalForm(processId: string, token: string): Promise<OnboardingFormDraft> {
    return this.requestPortal<null, OnboardingFormDraft>(processId + "/form", "GET", token);
  }

  async savePortalForm(processId: string, token: string, dto: SaveOnboardingFormDto): Promise<Record<string, unknown>> {
    return this.requestPortal<SaveOnboardingFormDto, Record<string, unknown>>(
      processId + "/form",
      "PUT",
      token,
      plainToInstance(SaveOnboardingFormDto, dto, { excludeExtraneousValues: true })
    );
  }

  async getPortalProcess(processId: string, token: string): Promise<EmployeeOnboardingProcessDto> {
    const process = await this.requestPortal<null, EmployeeOnboardingProcessDto>(processId, "GET", token);
    return plainToInstance(EmployeeOnboardingProcessDto, process);
  }

  async getPortalProgress(processId: string, token: string): Promise<OnboardingProgressDetailsDto> {
    const progressDetails = await this.requestPortal<null, OnboardingProgressDetailsDto>(
      processId + "/progress",
      "GET",
      token
    );
    return plainToInstance(OnboardingProgressDetailsDto, progressDetails);
  }

  async acceptSignatureWithFile(
    processId: string,
    requirementId: string,
    token: string,
    dto: AcceptSignatureRequirementDto,
    signatureFile: File
  ): Promise<EmployeeOnboardingAcceptanceDto> {
    const formData = new FormData();
    formData.append("consentText", dto.consentText);
    if (dto.formFields?.length) {
      formData.append("formFields", JSON.stringify(dto.formFields));
    }
    formData.append("signatureFile", signatureFile);

    const acceptance = await this.apiHttpClient.request<FormData, EmployeeOnboardingAcceptanceDto>(
      HttpRequest.create({
        url:
          this.resourceUrl + "/portal/processes/" + processId + "/signature-requirements/" + requirementId + "/accept",
        method: "POST",
        headers: { Authorization: "Bearer " + token },
        body: formData,
        type: "form-data",
      })
    );

    return plainToInstance(EmployeeOnboardingAcceptanceDto, acceptance);
  }

  async getPortalSignatureDownloadUrl(processId: string, requirementId: string, token: string): Promise<string> {
    const result = await this.apiHttpClient.request<null, { url: string }>(
      HttpRequest.create({
        url:
          this.resourceUrl +
          "/portal/processes/" +
          processId +
          "/signature-requirements/" +
          requirementId +
          "/download-url",
        headers: { Authorization: "Bearer " + token },
      })
    );

    return result.url;
  }

  async getPortalSignatureFormFields(
    processId: string,
    requirementId: string,
    token: string
  ): Promise<PdfAcroFormWidgetDto[]> {
    const fields = await this.apiHttpClient.request<null, PdfAcroFormWidgetDto[]>(
      HttpRequest.create({
        url:
          this.resourceUrl +
          "/portal/processes/" +
          processId +
          "/signature-requirements/" +
          requirementId +
          "/form-fields",
        headers: { Authorization: "Bearer " + token },
      })
    );

    return plainToInstance(PdfAcroFormWidgetDto, fields);
  }

  async getPortalSignaturePreview(processId: string, token: string): Promise<OnboardingSignaturePreviewDto> {
    const preview = await this.apiHttpClient.request<null, OnboardingSignaturePreviewDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/portal/processes/" + processId + "/signature-preview",
        headers: { Authorization: "Bearer " + token },
      })
    );

    return plainToInstance(OnboardingSignaturePreviewDto, preview);
  }

  async getPortalSignedDocumentDownloadUrl(processId: string, requirementId: string, token: string): Promise<string> {
    const result = await this.apiHttpClient.request<null, { url: string }>(
      HttpRequest.create({
        url:
          this.resourceUrl +
          "/portal/processes/" +
          processId +
          "/signature-requirements/" +
          requirementId +
          "/signed-download-url",
        headers: { Authorization: "Bearer " + token },
      })
    );

    return result.url;
  }

  async getSignatureRequirementDownloadUrl(processId: string, requirementId: string): Promise<string> {
    const result = await this.apiHttpClient.request<null, { url: string }>(
      HttpRequest.create({
        url:
          this.resourceUrl + "/processes/" + processId + "/signature-requirements/" + requirementId + "/download-url",
        auth: true,
      })
    );

    return result.url;
  }

  async getSignatureRequirementPlacements(processId: string, requirementId: string): Promise<SignaturePlacementDto[]> {
    const placements = await this.apiHttpClient.request<null, SignaturePlacementDto[]>(
      HttpRequest.create({
        url: this.resourceUrl + "/processes/" + processId + "/signature-requirements/" + requirementId + "/placements",
        auth: true,
      })
    );

    return plainToInstance(SignaturePlacementDto, placements);
  }

  async saveSignatureRequirementPlacements(
    processId: string,
    requirementId: string,
    dto: UpsertSignaturePlacementsDto
  ): Promise<SignaturePlacementDto[]> {
    const placements = await this.apiHttpClient.request<UpsertSignaturePlacementsDto, SignaturePlacementDto[]>(
      HttpRequest.create({
        url: this.resourceUrl + "/processes/" + processId + "/signature-requirements/" + requirementId + "/placements",
        method: "PUT",
        auth: true,
        body: plainToInstance(UpsertSignaturePlacementsDto, dto, { excludeExtraneousValues: true }),
      })
    );

    return plainToInstance(SignaturePlacementDto, placements);
  }

  async submitPortal(processId: string, token: string): Promise<{ submitted: true }> {
    return this.requestPortal<Record<string, never>, { submitted: true }>(processId + "/submit", "POST", token, {});
  }

  async uploadPortalDocument(
    processId: string,
    token: string,
    file: File,
    categoryId: string
  ): Promise<EmployeeDocumentVersionDto> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("categoryId", categoryId);

    const version = await this.apiHttpClient.request<FormData, EmployeeDocumentVersionDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/portal/processes/" + processId + "/documents",
        method: "POST",
        headers: { Authorization: "Bearer " + token },
        body: formData,
        type: "form-data",
      })
    );

    return plainToInstance(EmployeeDocumentVersionDto, version);
  }

  async uploadPortalFrontBackDocument(
    processId: string,
    token: string,
    frontImage: File,
    backImage: File,
    categoryId: string
  ): Promise<EmployeeDocumentVersionDto> {
    const formData = new FormData();
    formData.append("frontImage", frontImage);
    formData.append("backImage", backImage);
    formData.append("categoryId", categoryId);

    const version = await this.apiHttpClient.request<FormData, EmployeeDocumentVersionDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/portal/processes/" + processId + "/documents/front-back",
        method: "POST",
        headers: { Authorization: "Bearer " + token },
        body: formData,
        type: "form-data",
      })
    );

    return plainToInstance(EmployeeDocumentVersionDto, version);
  }

  async getPortalDocumentDownloadUrl(processId: string, categoryId: string, token: string): Promise<string> {
    const result = await this.apiHttpClient.request<null, { url: string }>(
      HttpRequest.create({
        url: this.resourceUrl + "/portal/processes/" + processId + "/documents/" + categoryId + "/download-url",
        headers: { Authorization: "Bearer " + token },
      })
    );

    return result.url;
  }

  async deletePortalDocument(processId: string, categoryId: string, token: string): Promise<void> {
    await this.apiHttpClient.request<null, void>(
      HttpRequest.create({
        url: this.resourceUrl + "/portal/processes/" + processId + "/documents/" + categoryId,
        method: "DELETE",
        type: "text",
        headers: { Authorization: "Bearer " + token },
      })
    );
  }

  async overrideSignatureRequirementFile(
    processId: string,
    requirementId: string,
    file: File
  ): Promise<EmployeeOnboardingSignatureRequirementDto> {
    const formData = new FormData();
    formData.append("file", file);

    const requirement = await this.apiHttpClient.request<FormData, EmployeeOnboardingSignatureRequirementDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/processes/" + processId + "/signature-requirements/" + requirementId + "/override",
        method: "POST",
        auth: true,
        body: formData,
        type: "form-data",
      })
    );

    return plainToInstance(EmployeeOnboardingSignatureRequirementDto, requirement);
  }

  private async requestPortal<RequestBody, ResponseData>(
    suffix: string,
    method: "GET" | "PUT" | "POST",
    token: string,
    body?: RequestBody
  ): Promise<ResponseData> {
    return this.apiHttpClient.request<RequestBody, ResponseData>(
      HttpRequest.create({
        url: this.resourceUrl + "/portal/processes/" + suffix,
        method,
        headers: { Authorization: "Bearer " + token },
        body,
      })
    );
  }
}
